export enum CosoMessageType {
  AI = 'ai',
  HUMAN = 'human'
}

export interface CosoMessage {
  type: CosoMessageType;
  date: Date;
  author: string;
  content?: string;
  files?: CosoHistoryFile[];
  streams?: CosoStream[];
  toolCalls?: CosoToolCall[];
  interrupts?: CosoInterrupt[];
  welcomeMsg?: boolean; // used to distinguish the welcome message from other AI messages
  reasoning?: CosoReasoning[];
  summarization?: boolean;
  errors?: CosoModelError[];
}

export enum CosoFileType {
  PDF = 'pdf',
  IMAGE = 'image',
  AUDIO = 'audio',
  VIDEO = 'video',
  UNSUPPORTED = 'unsupported'
}

export interface CosoFile {
  type: CosoFileType,
  fileName: string,
  mimeType: string,
  bucket: string,
  region: string,
  key: string,
  size: number,
}

export interface CosoLegacyFile {
  type: CosoFileType;
  fileName: string;
  mimeType: string;
  dataUrl: string;
}

export type CosoHistoryFile = CosoFile | CosoLegacyFile;

export enum CosoInteractionType {
  MESSAGE = 'message',
  INTERRUPT_RESPONSE = 'interrupt_response'
}

export interface CosoInteraction {
  type: CosoInteractionType;
  content?: string;
  files?: CosoFile[];
  decisions?: any[] // TODO: object with type:CosoDecisionType and message:string
  context?: CosoContext;
  sessionId?: string;
  userId?: number;
}

export type CosoReasoningEffort = 'minimal' | 'low' | 'medium' | 'high' | 'max';

export class CosoChat {
  coso_chats_id: number | undefined;
  id_user: number | undefined;
  c_date: string | Date | undefined;
  m_date: string | Date | undefined;
  chat_title: string | null | undefined;
  id_coso_config: number | undefined;
  language: string | null | undefined;
}

export interface CosoContext {
  project: string;
  coso_chats_id?: number;
  id_user?: number;
  user_fullname?: string;
  id_group?: number;
  is_admin?: boolean;
  id_coso_config?: number;
  module_name?: string | null;
  language?: string | null;
  reasoning_effort?: CosoReasoningEffort;
  api_key?: string; // TODO: implement an API-Key?
}

export enum CosoStreamType {
  TOOL_REQUEST = 'tool_request',
  TOOL_RESPONSE = 'tool_response',
  AI_PARTIAL_MESSAGE = 'ai_partial_message',
  AI_MESSAGE = 'ai_message',
  AI_AUDIO_CHUNK = 'ai_audio_chunk',
  AI_AUDIO_INTERRUPTED = 'ai_audio_interrupted',
  HUMAN_MESSAGE = 'human_message',
  INTERRUPT = 'interrupt',
  REASONING = 'reasoning',
  REMOVE_MESSAGE = 'remove_message',
  MODEL_ERROR = 'model_error',
}

export type CosoStream = CosoModelError | CosoInterrupt | CosoHumanMessage | CosoAIMessage
    | CosoAIPartialMessage | CosoAIAudioChunk | CosoAIAudioInterrupted | CosoToolRequest | CosoToolResponse | CosoReasoning | CosoRemoveMessage;

export interface CosoModelError {
  type: CosoStreamType.MODEL_ERROR,
  timestamp: string;
  content: string;
  usage_metadata: CosoUsageMetadata;
}

export interface CosoRemoveMessage {
  type: CosoStreamType.REMOVE_MESSAGE;
  messageId: string;
}

export interface CosoInterrupt {
  type: CosoStreamType.INTERRUPT;
  id: string;
  actionRequests: CosoActionRequest[];
}

export interface CosoReasoning {
  type: CosoStreamType.REASONING
  content: string
}

export enum CosoDecisionType {
  APPROVE = 'approve',
  EDIT = 'edit',
  REJECT = 'reject'
}

export interface CosoActionRequest {
  toolName: string,
  toolArgs: any,
  content: string,
  question?: string,
  display?: CosoActionRequestDisplay,
  allowedDecisions: CosoDecisionType[]
  decision?: CosoDecisionType;
}

export interface CosoActionRequestDisplay {
  title?: string;
  summary?: string;
  fields?: CosoActionRequestDisplayField[];
}

export interface CosoActionRequestDisplayField {
  label: string;
  value: any;
  type?: 'text' | 'url' | 'json';
}

export interface CosoHumanMessage {
  type: CosoStreamType.HUMAN_MESSAGE;
  content?: string;
  files: CosoHistoryFile[];
  timestamp: string;
  usage_metadata: CosoUsageMetadata;
  summarization: boolean;
}

export interface CosoAIPartialMessage {
  type: CosoStreamType.AI_PARTIAL_MESSAGE;
  content: string;
  timestamp: string;
  usage_metadata: CosoUsageMetadata;
}

export interface CosoAIMessage {
  type: CosoStreamType.AI_MESSAGE;
  content: string;
  timestamp: string;
  usage_metadata: CosoUsageMetadata;
}

export interface CosoAIAudioChunk {
  type: CosoStreamType.AI_AUDIO_CHUNK;
  data: string;
  mimeType: string;
}

export interface CosoAIAudioInterrupted {
  type: CosoStreamType.AI_AUDIO_INTERRUPTED;
}

export interface CosoToolRequest {
  type: CosoStreamType.TOOL_REQUEST;
  calls: CosoToolCall[];
  usage_metadata: CosoUsageMetadata;
}

export interface CosoToolResponse {
  type: CosoStreamType.TOOL_RESPONSE;
  calls: CosoToolCall[];
  usage_metadata: CosoUsageMetadata;
}

export interface CosoToolCall {
  id: string;
  toolName: string;
  toolArgs: any;
  valid: boolean;
  status: string;
  result: string;
  artifact: CosoToolCallArtifact;
}

export interface CosoToolCallArtifact {
  references?: CosoAttachmentReference[];
  generatedImage?: CosoGeneratedImageArtifact;
  generatedPdf?: CosoGeneratedPdfArtifact;
}

export interface CosoGeneratedImageArtifact {
  key: string;
  filename: string;
  mimeType: string;
}

export interface CosoGeneratedPdfArtifact {
  key: string;
  filename: string;
  mimeType: string;
}

export interface CosoAttachmentReference {
  table_name: string; // "projects", "repositories", etc...
  record_id: number; // id of the project/repository/etc...
  attachment_id: number;
  filename: string;
  reference_token: string;
}

export interface CosoUsageMetadata {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
}
